import { assertScenePlanV1 } from "./scene-plan.js";
import type { ScenePlan, ScenePlanSignature } from "./scene-plan.js";
import { interpretScenePlan } from "./scene-semantics.js";
import { createRendererFailure, createRendererFailureChannel } from "./renderer-contract.js";
import type {
  RendererDiagnostics,
  RendererFailure,
  RendererFailureInput,
  DomStyleSnapshot,
  SceneRenderer,
} from "./renderer-contract.js";

const DECORATIVE_NODE_COUNT = 4;
const SAFETY_STYLES = Object.freeze([
  ["pointer-events", "none"],
  ["animation", "none"],
  ["transition", "none"],
  ["user-select", "none"],
  ["touch-action", "none"],
] as const);

type StyleEntry = readonly [property: string, value: string];
type StyleMutation = readonly [element: HTMLElement, property: string, value: string];
interface StyleSnapshot {
  readonly property: string;
  readonly value: string;
  readonly priority: string;
}
interface ValidatedScenePlan {
  readonly plan: ScenePlan;
}

export interface DomRendererOptions {
  readonly getComputedStyle?: (element: Element) => { readonly position: string };
}

export class DomSceneRendererError extends Error {
  readonly failure: RendererFailure;

  constructor(input: RendererFailureInput) {
    const failure = createRendererFailure(input);
    super(failure.message);
    this.name = "DomSceneRendererError";
    this.failure = failure;
  }
}

function validateScenePlan(value: ScenePlan): ValidatedScenePlan {
  return Object.freeze({ plan: assertScenePlanV1(value) });
}

function setStyles(element: HTMLElement, entries: readonly StyleEntry[]): void {
  for (const [property, value] of entries) element.style.setProperty(property, value);
}

function markDecorative(element: HTMLElement): void {
  element.setAttribute("aria-hidden", "true");
  element.setAttribute("inert", "");
  element.setAttribute("role", "presentation");
  setStyles(element, SAFETY_STYLES);
}

interface DomPresentation {
  readonly mutations: readonly StyleMutation[];
  readonly rainCount: number;
  readonly rippleCount: number;
}

function cssNumber(value: number): string {
  return String(Number(value.toFixed(6)));
}

function colorWithAlpha(color: string, alpha: number): string {
  return `${color}${Math.round(alpha * 255).toString(16).padStart(2, "0")}`;
}

function cssColor(value: string): Readonly<{ color: string; alpha: number }> {
  const hexMatches = [...value.matchAll(/#([0-9a-f]{6})([0-9a-f]{2})?/gi)];
  const hex = hexMatches.at(-1) ?? null;
  if (hex !== null) {
    return Object.freeze({
      color: `#${hex[1].toLowerCase()}`,
      alpha: hex[2] === undefined ? 1 : Number.parseInt(hex[2], 16) / 255,
    });
  }
  const functionalMatches = [...value.matchAll(/rgba?\(([^)]+)\)/gi)];
  const functional = functionalMatches.at(-1) ?? null;
  if (functional !== null) {
    const channels = functional[1].match(/-?\d+(?:\.\d+)?/g)?.map(Number) ?? [];
    if (channels.length >= 3) {
      const color = `#${channels.slice(0, 3).map((channel) =>
        Math.round(channel).toString(16).padStart(2, "0")).join("")}`;
      return Object.freeze({ color, alpha: channels[3] ?? 1 });
    }
  }
  throw new Error(`Static DOM renderer could not read applied CSS color ${value}`);
}

function cssLayers(value: string): readonly string[] {
  const layers: string[] = [];
  let depth = 0;
  let start = 0;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === "(") depth += 1;
    else if (character === ")") depth -= 1;
    else if (character === "," && depth === 0) {
      layers.push(value.slice(start, index).trim());
      start = index + 1;
    }
  }
  layers.push(value.slice(start).trim());
  return layers.filter((layer) => layer !== "");
}

function pixelValues(value: string): readonly number[] {
  return [...value.matchAll(/(-?\d+(?:\.\d+)?)px/g)].map((match) => Number(match[1]));
}

function readAppliedPresentation(
  nodes: readonly HTMLElement[],
  rainCount: number,
  rippleCount: number,
): DomStyleSnapshot {
  const paperColor = cssColor(nodes[0].style.getPropertyValue("background-color"));
  const grainImage = nodes[0].style.getPropertyValue("background-image");
  const grainPixels = pixelValues(grainImage);
  const grainScalePx = grainPixels.at(-1);
  const grainColor = cssColor(grainImage);
  if (grainScalePx === undefined) throw new Error("Static DOM renderer lost paper grain geometry");

  const washRect = Object.freeze({
    x: Number.parseFloat(nodes[1].style.getPropertyValue("left")),
    y: Number.parseFloat(nodes[1].style.getPropertyValue("top")),
    width: Number.parseFloat(nodes[1].style.getPropertyValue("width")),
    height: Number.parseFloat(nodes[1].style.getPropertyValue("height")),
  });
  const washColor = cssColor(nodes[1].style.getPropertyValue("background-color"));
  const washOpacity = Number.parseFloat(nodes[1].style.getPropertyValue("opacity"));

  const rainImages = cssLayers(nodes[2].style.getPropertyValue("background-image"));
  const rainPositions = cssLayers(nodes[2].style.getPropertyValue("background-position"));
  const rainSizes = cssLayers(nodes[2].style.getPropertyValue("background-size"));
  const rippleImages = cssLayers(nodes[3].style.getPropertyValue("background-image"));
  const hostBounds = nodes[0].parentElement?.getBoundingClientRect();
  const bounds = Object.freeze({
    width: Math.max(1, hostBounds?.width ?? 1),
    height: Math.max(1, hostBounds?.height ?? 1),
    pixelRatio: 1,
  });
  if (rainImages.length !== rainCount
      || rainPositions.length !== rainCount
      || rainSizes.length !== rainCount
      || rippleImages.length !== rippleCount) {
    throw new Error("Static DOM renderer did not retain every signed composite primitive");
  }
  const rain = rainImages.map((image, index) => {
    const position = pixelValues(rainPositions[index]);
    const size = pixelValues(rainSizes[index]);
    if (position.length !== 2 || size.length !== 2) {
      throw new Error("Static DOM renderer lost rain composite geometry");
    }
    const cssAngle = Number(image.match(/linear-gradient\((-?\d+(?:\.\d+)?)deg/i)?.[1]);
    if (!Number.isFinite(cssAngle)) throw new Error("Static DOM renderer lost rain direction");
    const lineAngle = (cssAngle - 90) * Math.PI / 180;
    const direction = Math.cos(lineAngle) < 0 ? -1 : 1;
    const startX = direction < 0 ? position[0] + size[0] : position[0];
    const endX = direction < 0 ? position[0] : position[0] + size[0];
    const color = cssColor(image);
    return Object.freeze({
      startX,
      startY: position[1],
      endX,
      endY: position[1] + size[1],
      color: color.color,
      alpha: color.alpha,
    });
  });
  const ripples = rippleImages.map((image) => {
    const geometry = pixelValues(image);
    if (geometry.length < 6) throw new Error("Static DOM renderer lost ripple composite geometry");
    const color = cssColor(image);
    return Object.freeze({
      centerX: geometry[0],
      centerY: geometry[1],
      radius: (geometry[2] + geometry[4]) / 2,
      color: color.color,
      alpha: color.alpha,
    });
  });
  return Object.freeze({
    kind: "dom-styles",
    bounds,
    paper: Object.freeze({
      color: paperColor.color,
      grainColor: grainColor.color,
      grainOpacity: grainColor.alpha,
      grainScalePx,
    }),
    wash: Object.freeze({
      color: washColor.color,
      opacity: washOpacity,
      coverage: washRect.width / bounds.width,
      rect: washRect,
    }),
    rain: Object.freeze(rain),
    ripples: Object.freeze(ripples),
  });
}

function styleMutations(validated: ValidatedScenePlan, nodes: readonly HTMLElement[]): DomPresentation {
  const { plan } = validated;
  const bounds = nodes[0].parentElement?.getBoundingClientRect();
  const viewportWidth = Math.max(1, bounds?.width ?? 1);
  const viewportHeight = Math.max(1, bounds?.height ?? 1);
  const renderState = interpretScenePlan(plan, {
    width: viewportWidth,
    height: viewportHeight,
    pixelRatio: 1,
  }, 0);
  const grainAlpha = Math.round(renderState.paper.grainOpacity * 255)
    .toString(16).padStart(2, "0");
  const rainImages: string[] = [];
  const rainPositions: string[] = [];
  const rainSizes: string[] = [];
  for (const segment of renderState.rain) {
    const deltaX = segment.endX - segment.startX;
    const deltaY = segment.endY - segment.startY;
    const lineAngle = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
    const color = colorWithAlpha(plan.palette.ink, segment.alpha);
    rainImages.push(`linear-gradient(${cssNumber(lineAngle + 90)}deg, transparent calc(50% - 0.5px), ${color} calc(50% - 0.5px), ${color} calc(50% + 0.5px), transparent calc(50% + 0.5px))`);
    rainPositions.push(`${cssNumber(Math.min(segment.startX, segment.endX))}px ${cssNumber(Math.min(segment.startY, segment.endY))}px`);
    rainSizes.push(`${cssNumber(Math.max(1, Math.abs(deltaX)))}px ${cssNumber(Math.max(1, Math.abs(deltaY)))}px`);
  }
  const rippleImages = renderState.ripples.map((ripple) => {
    const innerRadius = Math.max(0, ripple.radius - 0.5);
    const outerRadius = ripple.radius + 0.5;
    const color = colorWithAlpha(plan.palette.accent, ripple.alpha);
    return `radial-gradient(circle at ${cssNumber(ripple.centerX)}px ${cssNumber(ripple.centerY)}px, transparent ${cssNumber(innerRadius)}px, ${color} ${cssNumber(innerRadius)}px, ${color} ${cssNumber(outerRadius)}px, transparent ${cssNumber(outerRadius)}px)`;
  });
  const mutations: StyleMutation[] = [
    [nodes[0], "background-color", renderState.paper.color],
    [nodes[0], "background-image", `repeating-linear-gradient(to bottom, ${renderState.paper.grainColor}${grainAlpha} 0px, ${renderState.paper.grainColor}${grainAlpha} ${renderState.paper.pattern.rowHeightPx}px, transparent ${renderState.paper.pattern.rowHeightPx}px, transparent ${renderState.paper.grainScalePx}px)`],
    [nodes[0], "color", plan.palette.ink],
    [nodes[1], "position", "absolute"],
    [nodes[1], "left", `${renderState.wash.rect.x}px`],
    [nodes[1], "top", `${renderState.wash.rect.y}px`],
    [nodes[1], "width", `${renderState.wash.rect.width}px`],
    [nodes[1], "height", `${renderState.wash.rect.height}px`],
    [nodes[1], "background-color", renderState.wash.color],
    [nodes[1], "opacity", String(renderState.wash.opacity)],
    [nodes[2], "position", "absolute"],
    [nodes[2], "inset", "0"],
    [nodes[2], "background-image", rainImages.join(", ")],
    [nodes[2], "background-position", rainPositions.join(", ")],
    [nodes[2], "background-size", rainSizes.join(", ")],
    [nodes[2], "background-repeat", "no-repeat"],
    [nodes[3], "position", "absolute"],
    [nodes[3], "inset", "0"],
    [nodes[3], "background-image", rippleImages.join(", ")],
    [nodes[3], "background-repeat", "no-repeat"],
  ];
  return Object.freeze({
    mutations: Object.freeze(mutations),
    rainCount: renderState.rain.length,
    rippleCount: renderState.ripples.length,
  });
}

function applyPresentation(
  validated: ValidatedScenePlan,
  nodes: readonly HTMLElement[],
): DomStyleSnapshot {
  const presentation = styleMutations(validated, nodes);
  const { mutations } = presentation;
  const previous = mutations.map(([element, property]) => ({
    element,
    property,
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
  }));
  try {
    for (const [element, property, value] of mutations) element.style.setProperty(property, value);
    if (mutations.some(([element, property, value]) =>
          value !== "" && element.style.getPropertyValue(property) === "")) {
      throw new Error("Static DOM renderer could not verify applied style resources");
    }
    return readAppliedPresentation(
      nodes,
      presentation.rainCount,
      presentation.rippleCount,
    );
  } catch (cause) {
    for (const snapshot of previous) {
      try {
        if (snapshot.value === "") snapshot.element.style.removeProperty(snapshot.property);
        else snapshot.element.style.setProperty(snapshot.property, snapshot.value, snapshot.priority);
      } catch {
        // Best-effort rollback preserves the original operational failure.
      }
    }
    throw cause;
  }
}

function snapshotStyles(
  element: HTMLElement,
  properties: readonly string[],
): readonly StyleSnapshot[] {
  return properties.map((property) => Object.freeze({
    property,
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
  }));
}

function restoreStyles(element: HTMLElement, snapshots: readonly StyleSnapshot[]): void {
  let firstFailure: unknown;
  for (const snapshot of snapshots) {
    try {
      if (snapshot.value === "") element.style.removeProperty(snapshot.property);
      else element.style.setProperty(snapshot.property, snapshot.value, snapshot.priority);
    } catch (cause) {
      firstFailure ??= cause;
    }
  }
  if (firstFailure !== undefined) {
    throw new Error("Static DOM renderer style restoration was incomplete", { cause: firstFailure });
  }
}

function readComputedPosition(host: HTMLElement, options: DomRendererOptions): string {
  if (options.getComputedStyle !== undefined) return options.getComputedStyle(host).position;
  const view = host.ownerDocument.defaultView;
  if (view !== null) return view.getComputedStyle(host).position;
  if (typeof globalThis.getComputedStyle === "function") {
    return globalThis.getComputedStyle(host).position;
  }
  throw new TypeError("DOM renderer requires browser or injected computed style access");
}

export function createDomSceneRenderer(
  host: HTMLElement,
  options: DomRendererOptions = {},
): SceneRenderer {
  const document = host.ownerDocument;
  if (document === null || typeof document.createElement !== "function") {
    throw new TypeError("DOM renderer host must belong to a document");
  }

  const ownsPosition = readComputedPosition(host, options) === "static";
  const hostProperties = ownsPosition
    ? Object.freeze(["position", "isolation"] as const)
    : Object.freeze(["isolation"] as const);
  const hostStyles = snapshotStyles(host, hostProperties);
  const nodes = Array.from({ length: DECORATIVE_NODE_COUNT }, () => {
    const element = document.createElement("div");
    markDecorative(element);
    return element;
  });
  const [layer, ...children] = nodes;
  layer.setAttribute("data-north-star-renderer", "dom");
  setStyles(layer, [
    ["position", "absolute"],
    ["inset", "0"],
    ["overflow", "hidden"],
    ["contain", "strict"],
    ["isolation", "isolate"],
    ["z-index", "-1"],
  ]);
  layer.append(...children);
  try {
    setStyles(host, ownsPosition
      ? [["position", "relative"], ["isolation", "isolate"]]
      : [["isolation", "isolate"]]);
    host.append(layer);
  } catch (cause) {
    let cleanupCause: unknown;
    try { layer.remove(); } catch (failure) { cleanupCause ??= failure; }
    try { restoreStyles(host, hostStyles); } catch (failure) { cleanupCause ??= failure; }
    const hostRestored = hostStyles.every((snapshot) =>
      host.style.getPropertyValue(snapshot.property) === snapshot.value
      && host.style.getPropertyPriority(snapshot.property) === snapshot.priority);
    if (cleanupCause !== undefined || layer.parentNode !== null || !hostRestored) {
      throw new DomSceneRendererError({
        kind: "dom",
        phase: "initialize",
        code: "resource",
        message: "Static DOM renderer construction rollback was incomplete",
        recoverable: false,
        cause: cleanupCause ?? cause,
      });
    }
    throw new DomSceneRendererError({
      kind: "dom",
      phase: "initialize",
      code: "initialization",
      message: "Static DOM renderer construction failed",
      recoverable: false,
      cause,
    });
  }

  const channel = createRendererFailureChannel();
  let signature: ScenePlanSignature | null = null;
  let acceptingApply = true;
  let layerAttached = true;
  let hostContextPending = true;
  let cleanupComplete = false;
  let renderState: DomStyleSnapshot | null = null;
  let applyCount = 0;
  let releaseAttempts = 0;
  let releaseFailures = 0;

  return Object.freeze({
    kind: "dom" as const,
    apply(nextScenePlan: ScenePlan): void {
      if (!acceptingApply) return;
      let validated: ValidatedScenePlan;
      try {
        validated = validateScenePlan(nextScenePlan);
      } catch (cause) {
        channel.emit({
          kind: "dom",
          phase: "apply",
          code: "apply",
          message: "Static DOM renderer rejected an invalid ScenePlan contract",
          recoverable: false,
          cause,
        });
        return;
      }
      try {
        const appliedRenderState = applyPresentation(validated, nodes);
        signature = validated.plan.signature;
        renderState = appliedRenderState;
        applyCount += 1;
      } catch (cause) {
        channel.emit({
          kind: "dom",
          phase: "apply",
          code: "resource",
          message: "Static DOM renderer could not apply DOM style resources",
          recoverable: true,
          cause,
        });
      }
    },
    frame(_nowMs: number): void {
      // Static fallback: deliberately no scheduling, mutation, or frame work.
    },
    onFailure(listener: (failure: RendererFailure) => void): () => void {
      return channel.onFailure(listener);
    },
    diagnostics(): RendererDiagnostics {
      const objectCount = layerAttached ? nodes.length : 0;
      const activeSubscriptions = channel.activeSubscriptions();
      const bounds = host.getBoundingClientRect?.();
      return Object.freeze({
        kind: "dom",
        signature,
        framePolicy: "none",
        canvasCount: 0,
        objectCount,
        activeSubscriptions,
        allocationsAfterApply: 0,
        lastFailure: channel.lastFailure(),
        observation: Object.freeze({
          kind: "dom",
          applyCount,
          frameCount: 0,
          renderCount: applyCount,
          renderState: cleanupComplete ? null : renderState,
          viewport: Object.freeze({
            width: Math.max(1, bounds?.width ?? 1),
            height: Math.max(1, bounds?.height ?? 1),
            pixelRatio: 1,
          }),
          resources: Object.freeze({
            canvasCount: 0,
            objectCount,
            activeSubscriptions,
            releaseAttempts,
            releaseFailures,
          }),
        }),
      });
    },
    destroy(): void {
      if (cleanupComplete) return;
      acceptingApply = false;
      let cleanupFailed = false;
      if (layerAttached) {
        releaseAttempts += 1;
        try {
          layer.remove();
          layerAttached = false;
          signature = null;
          renderState = null;
        } catch (cause) {
          cleanupFailed = true;
          releaseFailures += 1;
          channel.emit({
            kind: "dom",
            phase: "destroy",
            code: "cleanup",
            message: "Static DOM renderer could not remove its owned decorative layer",
            recoverable: true,
            cause,
          });
        }
      }
      if (hostContextPending) {
        releaseAttempts += 1;
        try {
          restoreStyles(host, hostStyles);
          hostContextPending = false;
        } catch (cause) {
          cleanupFailed = true;
          releaseFailures += 1;
          channel.emit({
            kind: "dom",
            phase: "destroy",
            code: "cleanup",
            message: "Static DOM renderer could not restore its host context",
            recoverable: true,
            cause,
          });
        }
      }
      if (!layerAttached && !hostContextPending && !cleanupFailed) {
        cleanupComplete = true;
        channel.destroy();
      }
    },
  });
}

export const createDomRenderer = createDomSceneRenderer;
